import { useCallback, useRef } from 'react';
import { useLocation } from 'wouter';

import { createAudio } from '@/lib/api';
import { resolvePatientDbId } from '@/lib/sessionUtils';
import type { Patient, SessionMode } from '@/types/session';

export interface UseSessionOptions {
  mode: SessionMode;
  /** Language for the created session. */
  language: string;
  /**
   * Resolve the currently selected patient at call time. A getter (rather than a
   * value) lets pages that hold the patient in a ref pass the live value without
   * re-creating the hook on every selection change.
   */
  getPatient?: () => Patient | undefined;
  /** Retain the resolved DB identity for task generation and session resume, including uploads. */
  onPatientResolved?: (patientId: string, dbId: number) => void;
  /** When resuming an existing session, seed its id so no new one is minted. */
  resumeSessionId?: string;
}

export interface EnsureSessionResult {
  session_id: string;
  patient: Patient | undefined;
}

/**
 * Consolidates the "mint a session id, navigate to its URL, and POST /api/sessions"
 * flow that dictation, conversation, and the upload dialog all need.
 *
 * `ensureSession` is idempotent: the first call mints a UUID (matching the URL),
 * resolves the patient's db id, and creates the audio record; later calls return
 * the same id without re-creating it. Identity is taken from the auth session
 * cookie server-side — no user/clinic id is ever sent in the body.
 */
export const useSession = (opts: UseSessionOptions) => {
  const [, navigate] = useLocation();
  const sessionIdRef = useRef<string>(opts.resumeSessionId ?? '');
  const createdRef = useRef<boolean>(Boolean(opts.resumeSessionId));

  const ensureSession = useCallback(async (): Promise<EnsureSessionResult> => {
    const currentPatient = opts.getPatient?.();

    if (sessionIdRef.current && createdRef.current) {
      return { session_id: sessionIdRef.current, patient: currentPatient };
    }

    if (!sessionIdRef.current) {
      sessionIdRef.current = crypto.randomUUID();
      // Update the URL so a refresh reloads this session.
      navigate(`/${opts.mode}/${sessionIdRef.current}`, { replace: true });
    }

    // Resolve the patient's transcriber_db id before creating the session.
    let resolvedPatient = currentPatient;
    if (resolvedPatient && !resolvedPatient.dbId) {
      resolvedPatient = await resolvePatientDbId(resolvedPatient);
    }

    await createAudio({
      id: sessionIdRef.current,
      language: opts.language,
      diarized: opts.mode === 'conversation',
      patient_id: resolvedPatient?.dbId != null ? String(resolvedPatient.dbId) : undefined,
      patient_ehr_id: resolvedPatient?.fhirPatientId,
      patient_name: resolvedPatient?.name,
    });

    if (resolvedPatient?.dbId != null) opts.onPatientResolved?.(resolvedPatient.id, resolvedPatient.dbId);
    createdRef.current = true;
    return { session_id: sessionIdRef.current, patient: resolvedPatient };
  }, [opts.mode, opts.language, opts.getPatient, opts.onPatientResolved, navigate]);

  return { sessionIdRef, ensureSession };
};
